<template>
  <app-page title="Recognition" subtitle="Create, manage and report on your awards programme." icon="gift">
    <template v-slot:header-content v-if="recognitionPrograms && recognitionPrograms.length > 0">
      <app-stat
        :stat="recognitionLedger?.balance_minor ?? 0"
        :formatter="getCurrencyFormatter(recognitionLedger?.balance_minor ?? 0)"
        title="Balance"
        color="default"
        small
        class="header-stat"
      >
        <template #title-action>
          <router-link :to="{ name: 'recognition-settings' }" class="add-funds-link">
            <b-button variant="link" size="sm" class="p-0">
              <font-awesome-icon :icon="['fal', 'plus']" class="mr-1" />
              Add funds
            </b-button>
          </router-link>
        </template>
      </app-stat>
    </template>

    <template v-slot:after-header v-if="recognitionPrograms && recognitionPrograms.length > 0">
      <b-row no-gutters class="justify-content-end mt-4">
        <b-button variant="outline-primary" v-b-modal.give-recognition-modal class="mr-3"> Give recognition </b-button>
        <b-button variant="primary" v-b-modal.create-award-modal>
          <font-awesome-icon :icon="['fal', 'plus']" class="mr-2" />
          Create award
        </b-button>
      </b-row>
    </template>

    <template v-slot:custom-content>
      <app-loading :loading="isLoading" is-background />

      <template v-if="!isLoading">
        <!-- Empty state: show intro card when there are no awards/programs -->
        <b-col v-if="!recognitionPrograms || recognitionPrograms.length === 0">
          <b-card no-body>
            <b-row no-gutters>
              <b-col md="6">
                <b-card-body class="p-6 card-body d-flex flex-column justify-content-between">
                  <div>
                    <h3 class="font-weight-bold mb-3">Launch your first reward</h3>
                    <p>
                      Turn great work into moments worth celebrating, build a quick award and send it when someone
                      shines.
                    </p>
                  </div>

                  <b-button class="card-button" variant="outline-secondary" v-b-modal.create-award-modal>
                    <font-awesome-icon :icon="['fal', 'plus']" class="mr-2" /><strong>Create award</strong>
                  </b-button>
                </b-card-body>
              </b-col>

              <b-col md="6" class="d-flex justify-content-end">
                <b-card-img :src="recognitionImg" alt="Image" class="recognition-image"></b-card-img>
              </b-col>
            </b-row>
          </b-card>
        </b-col>

        <!-- Tabbed interface when programs exist -->
        <b-col v-else>
          <sme-alert v-if="shortfallMinor > 0" level="warning" class="mb-3">
            <div class="shortfall-alert">
              <span>
                You're <strong>{{ formatShortfall(shortfallMinor) }}</strong> short of the
                <strong>{{ formatShortfall(totalUpcomingCostMinor) }}</strong> scheduled in the next 30 days. Add
                <strong>{{ formatShortfall(shortfallMinor) }}</strong> to cover upcoming rewards.
              </span>
              <router-link :to="{ name: 'recognition-settings' }">Add funds</router-link>
            </div>
          </sme-alert>
          <sme-card large>
            <navigable-tabs content-class="mt-3">
              <template #tabs-end>
                <b-form-checkbox
                  v-if="activeTab === 'awards'"
                  v-model="showDisabledAwards"
                  switch
                  size="sm"
                  class="show-disabled-toggle"
                >
                  Show disabled awards
                </b-form-checkbox>
              </template>
              <b-tab id="awards">
                <template #title>
                  <span>Awards</span>
                </template>
                <awards-table
                  :programs="recognitionPrograms"
                  :loading="isLoadingPrograms"
                  :pending-nominations="pendingNominations"
                  :toggling-program-id="togglingProgramId"
                  :show-disabled-awards="showDisabledAwards"
                  @toggle-active="onToggleActive"
                />
              </b-tab>

              <b-tab id="pending-approval" @shown="onPendingTabShown">
                <template #title>
                  <span>
                    Pending approval
                    <b-badge v-if="pendingNominations.length > 0" pill variant="primary" class="ml-1">
                      {{ pendingNominations.length }}
                    </b-badge>
                  </span>
                </template>
                <pending-approvals-table
                  :nominations="pendingNominations"
                  :loading="isLoadingPendingNominations"
                  @review="onReviewNomination"
                />
              </b-tab>

              <b-tab id="upcoming" @shown="onUpcomingTabShown">
                <template #title>
                  <span>
                    Upcoming
                    <b-badge v-if="upcomingAwards.length > 0" pill variant="primary" class="ml-1">
                      {{ upcomingAwards.length }}
                    </b-badge>
                  </span>
                </template>
                <upcoming-awards-table
                  :awards="upcomingAwards"
                  :loading="isLoadingUpcomingAwards"
                  @change="onUpcomingWindowChange"
                />
              </b-tab>

              <b-tab id="analytics">
                <template #title>
                  <span>Analytics</span>
                </template>
                <analytics-tab :programs="recognitionPrograms" />
              </b-tab>

              <b-tab id="activity">
                <template #title>
                  <span>Activity</span>
                </template>
                <activity-table :programs="recognitionPrograms" />
              </b-tab>

              <b-tab id="transactions">
                <template #title>
                  <span>Transactions</span>
                </template>
                <transactions-table :programs="recognitionPrograms" />
              </b-tab>
            </navigable-tabs>
          </sme-card>
        </b-col>
      </template>
    </template>

    <template v-slot:footer>
      <create-award-modal @created="onProgramCreated" />
      <give-recognition-modal :programs="recognitionPrograms" @awarded="onAwardGiven" />
      <review-nomination-modal
        ref="reviewModalRef"
        :nomination="reviewingNomination"
        :programs="recognitionPrograms"
        @approved="onNominationReviewed"
        @rejected="onNominationReviewed"
      />
      <confirm-disable-award-modal
        ref="confirmDisableModalRef"
        :program-name="togglingProgramName"
        @confirmed="onDisableConfirmed"
      />
    </template>
  </app-page>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import AppPage from '@/components/AppPage.vue';
import AppStat from '@/components/AppStat.vue';
import AppLoading from '@/components/AppLoading.vue';
import NavigableTabs from '@/components/NavigableTabs.vue';
import SmeCard from '@/components/atoms/SmeCard.vue';
import CreateAwardModal from '@/pages/recognition/components/CreateAwardModal.vue';
import GiveRecognitionModal from '@/pages/recognition/components/GiveRecognitionModal.vue';
import ReviewNominationModal from '@/pages/recognition/components/ReviewNominationModal.vue';
import ConfirmDisableAwardModal from '@/pages/recognition/components/ConfirmDisableAwardModal.vue';
import AwardsTable from '@/pages/recognition/components/AwardsTable.vue';
import PendingApprovalsTable from '@/pages/recognition/components/PendingApprovalsTable.vue';
import UpcomingAwardsTable from '@/pages/recognition/components/UpcomingAwardsTable.vue';
import TransactionsTable from '@/pages/recognition/components/TransactionsTable.vue';
import ActivityTable from '@/pages/recognition/components/ActivityTable.vue';
import AnalyticsTab from '@/pages/recognition/components/AnalyticsTab.vue';
import { getCurrencyFormatter, toCurrency } from '@/utils';
import ApiClient from '@/ApiClient';
import State from '@/state/State';
import SmeAlert from '@/components/atoms/SmeAlert.vue';
import useToast from '@/composables/useToast';
import recognitionImg from '@/assets/recognition-jigsaw-pieces.png';
import { useRoute } from 'vue-router/composables';

const { doToast, TOAST_VARIANT } = useToast();

const $route = useRoute();
// "Show disabled awards" only affects the Awards tab; hide it elsewhere.
const activeTab = computed(() => $route.query.tab || 'awards');

const reviewModalRef = ref(null);
const confirmDisableModalRef = ref(null);
const reviewingNomination = ref(null);
const togglingProgramId = ref(null);
const togglingProgramName = ref('');
const SHOW_DISABLED_AWARDS_STORAGE_KEY = 'sme.recognition.showDisabledAwards';
const storedShowDisabledAwards = localStorage.getItem(SHOW_DISABLED_AWARDS_STORAGE_KEY);
const showDisabledAwards = ref(storedShowDisabledAwards === null ? true : storedShowDisabledAwards === 'true');
watch(showDisabledAwards, value => {
  localStorage.setItem(SHOW_DISABLED_AWARDS_STORAGE_KEY, String(value));
});
const recognitionPrograms = ref([]);
const recognitionLedger = ref({});
const pendingNominations = ref([]);
const upcomingAwards = ref([]);
const isLoadingLedger = ref(true);
const isLoadingPrograms = ref(true);
const isLoadingPendingNominations = ref(false);
const isLoadingUpcomingAwards = ref(false);
const pendingNominationsLoaded = ref(false);
const upcomingAwardsLoaded = ref(false);
const isLoading = computed(() => isLoadingLedger.value || isLoadingPrograms.value);

const currency = computed(() => State.state?.company?.default_currency);

const totalUpcomingCostMinor = computed(() => {
  return upcomingAwards.value.reduce((sum, award) => sum + (award.amount_minor || 0), 0);
});

const shortfallMinor = computed(() => {
  const balance = recognitionLedger.value?.balance_minor ?? 0;
  return Math.max(0, totalUpcomingCostMinor.value - balance);
});

const formatShortfall = value => toCurrency(value, currency.value).replace(/\.00$/, '');

const getRecognitionLedger = async () => {
  isLoadingLedger.value = true;
  try {
    const ledger = await ApiClient.getRecognitionLedger();
    recognitionLedger.value = ledger;
  } catch (e) {
    recognitionLedger.value = {};
  } finally {
    isLoadingLedger.value = false;
  }
};

const getRecognitionPrograms = async () => {
  isLoadingPrograms.value = true;
  try {
    const programs = await ApiClient.getRecognitionPrograms({ includeInactive: true });
    recognitionPrograms.value = programs;
  } catch (e) {
    recognitionPrograms.value = [];
  } finally {
    isLoadingPrograms.value = false;
  }
};

const getPendingNominations = async () => {
  isLoadingPendingNominations.value = true;
  try {
    const nominations = await ApiClient.getPendingNominations();
    pendingNominations.value = nominations || [];
    pendingNominationsLoaded.value = true;
  } catch (e) {
    pendingNominations.value = [];
  } finally {
    isLoadingPendingNominations.value = false;
  }
};

const onPendingTabShown = () => {
  // Load pending nominations when tab is first shown
  if (!pendingNominationsLoaded.value) {
    getPendingNominations();
  }
};

const upcomingWindowDateTo = ref(null);

const getUpcomingAwards = async () => {
  isLoadingUpcomingAwards.value = true;
  try {
    const awards = await ApiClient.getUpcomingMilestoneAwards(null, upcomingWindowDateTo.value);
    upcomingAwards.value = awards || [];
    upcomingAwardsLoaded.value = true;
  } catch (e) {
    upcomingAwards.value = [];
  } finally {
    isLoadingUpcomingAwards.value = false;
  }
};

const onUpcomingTabShown = () => {
  // Load upcoming awards when tab is first shown
  if (!upcomingAwardsLoaded.value) {
    getUpcomingAwards();
  }
};

const onUpcomingWindowChange = dateTo => {
  upcomingWindowDateTo.value = dateTo;
  getUpcomingAwards();
};

const onProgramCreated = () => {
  getRecognitionPrograms();
  // Reset loaded flags so tabs will refetch data for the new program
  pendingNominationsLoaded.value = false;
  upcomingAwardsLoaded.value = false;
};

const onAwardGiven = () => {
  getRecognitionLedger();
  getRecognitionPrograms();
  getPendingNominations();
  getUpcomingAwards();
};

const onReviewNomination = nomination => {
  reviewingNomination.value = nomination;
  reviewModalRef.value?.show();
};

const onNominationReviewed = () => {
  getRecognitionLedger();
  getPendingNominations();
};

const onToggleActive = ({ programId, programName, currentIsActive }) => {
  if (currentIsActive) {
    // Disabling -- show confirmation
    togglingProgramId.value = programId;
    togglingProgramName.value = programName;
    confirmDisableModalRef.value?.show();
  } else {
    // Enabling -- no confirmation needed
    updateProgramActiveStatus(programId, true);
  }
};

const onDisableConfirmed = () => {
  if (togglingProgramId.value) {
    updateProgramActiveStatus(togglingProgramId.value, false);
  }
};

const updateProgramActiveStatus = async (programId, isActive) => {
  togglingProgramId.value = programId;
  try {
    await ApiClient.updateRecognitionProgram(programId, { is_active: isActive });
    doToast('Success', `Award ${isActive ? 'enabled' : 'disabled'} successfully`, TOAST_VARIANT.SUCCESS);
    await getRecognitionPrograms();
  } catch (e) {
    const message = e?.response?.data?.message || e?.message || `Failed to ${isActive ? 'enable' : 'disable'} award`;
    doToast('Error', message, TOAST_VARIANT.DANGER);
  } finally {
    togglingProgramId.value = null;
    togglingProgramName.value = '';
  }
};

onMounted(async () => {
  await Promise.all([getRecognitionLedger(), getRecognitionPrograms(), getPendingNominations(), getUpcomingAwards()]);
});
</script>

<style scoped lang="scss">
.header-stat {
  height: auto;

  :deep(.app-stat__value-container) {
    width: 16rem;
    justify-content: flex-start;
  }

  :deep(.app-stat__description) {
    color: var(--palette-color-default-lighten-30);
  }
}

.card-body {
  height: 100%;
  color: var(--palette-color-default);
}

.card-button {
  color: var(--palette-color-default);
  width: fit-content;
}

.recognition-image {
  @media (max-width: 992px) {
    display: none;
    visibility: hidden;
  }

  width: 420px;
  height: 240px;
  object-fit: cover;
  object-position: center;
  display: block;
}

.add-funds-link {
  text-decoration: none;

  &:hover {
    text-decoration: none;
  }
}

.shortfall-alert {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}

.show-disabled-toggle {
  display: flex;
  align-items: center;
  white-space: nowrap;
  margin-left: auto;
  font-size: 0.875rem;
}

.tab-placeholder {
  padding: 3rem;
  text-align: center;
}
</style>
